
import { computed, defineComponent, ref, watch } from 'vue'

import SaveChangesBar from '@/components/SaveChangesBar.vue'
import { useStore } from '@/composables/use-store'
import Draggable from 'vuedraggable'
import { Computed } from 'vuex'
import { CampaignSocialLink } from '@/types/campaigns'

export default defineComponent({
  components: {
    Draggable,
    SaveChangesBar
  },

  setup () {
    const store = useStore()

    const currentCampaignLinks = computed<CampaignSocialLink[] | null>(
      () => store.getters['campaigns/current'].data.social_links
    )

    const locales = computed<string[]>(() => store.getters['campaigns/currentLocaleCodes'])

    const socialOptionsMap: Record<string, string> = {
      apple_music: 'Apple Music',
      spotify: 'Spotify',
      youtube: 'YouTube',
      instagram: 'Instagram',
      twitter: 'Twitter',
      facebook: 'Facebook',
      tiktok: 'TikTok',
      discord: 'Discord',
      deezer: 'Deezer',
      amazon_music: 'Amazon Music'
    }

    const socialUrlPlaceholders: Record<string, string> = {
      apple_music: 'https://music.apple.com/artist/...',
      spotify: 'https://open.spotify.com/artist/...',
      youtube: 'https://www.youtube.com/channel/...',
      instagram: 'https://www.instagram.com/...',
      twitter: 'https://twitter.com/...',
      facebook: 'https://www.facebook.com/...',
      tiktok: 'https://www.tiktok.com/@...',
      discord: 'https://discord.gg/...',
      deezer: 'https://www.deezer.com/en/artist/...',
      amazon_music: 'https://music.amazon.com/artists/...'
    }

    const socialOptions = computed(() => {
      return Object.entries(socialOptionsMap).map(([key, value]) => ({
        text: value,
        value: key
      })).sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    })

    const currentNewSocialKey = ref<string | null>(null)
    const currentNewSocial = ref<CampaignSocialLink | null>(null)
    const currentNewSocialName = computed(() => {
      return currentNewSocial.value ? socialOptionsMap[currentNewSocial.value.name] : null
    })

    // watch currentNewSocialKey to update currentNewSocial
    watch(currentNewSocialKey, (newKey: string|null) => {
      if (newKey) {
        currentNewSocial.value = {
          name: newKey,
          urls: Object.fromEntries(locales.value.map(locale => [locale, '']))
        }
      } else {
        currentNewSocial.value = null
      }
    })

    // TODO: Get from store
    const socialLinks = ref<CampaignSocialLink[]>(JSON.parse(JSON.stringify(currentCampaignLinks.value || [])))
    const editingIndex = ref<number | null>(null)

    const addSocialLink = () => {
      if (currentNewSocial.value) {
        socialLinks.value.push(currentNewSocial.value)
        currentNewSocialKey.value = null
        currentNewSocial.value = null
      }
    }

    const unsavedChanges = computed<boolean>(() => {
      const origEmpty = !currentCampaignLinks.value || currentCampaignLinks.value.length === 0
      const newEmpty = socialLinks.value.length === 0
      if (origEmpty && newEmpty) {
        return false
      } else if (origEmpty || newEmpty) {
        // if only one is empty, then there are unsaved changes
        return true
      } else if (currentCampaignLinks.value.length !== socialLinks.value.length) {
        // if the lengths are different, then there are unsaved changes
        return true
      }
      // compare each link
      for (let i = 0; i < socialLinks.value.length; i++) {
        const origLink = currentCampaignLinks.value[i]
        const newLink = socialLinks.value[i]
        if (origLink.name !== newLink.name) {
          return true
        }
        for (const locale of locales.value) {
          if (origLink.urls[locale] !== newLink.urls[locale]) {
            return true
          }
        }
      }
      return false
    })

    const saveChanges = () => store.dispatch('campaigns/EDIT_CURRENT', { social_links: socialLinks.value })

    return {
      locales,
      socialOptions,
      currentNewSocial,
      currentNewSocialName,
      socialLinks,
      socialOptionsMap,
      currentNewSocialKey,
      addSocialLink,
      editingIndex,
      unsavedChanges,
      saveChanges,
      currentCampaignLinks,
      socialUrlPlaceholders
    }
  }
})

